import { ReactElement, useMemo } from 'react';
import { Trans } from 'react-i18next';

import { Translate, useTranslate } from '@dreryk/react-common';
import { ModalConfirmButton, StrongText, StyledFlex } from '@dreryk/react-components';

import { draftPrescriptionsTranslationKeys } from '../../../../../../translations';
import { DraftPrescriptionsFieldArray } from '../../DraftPrescriptionsForm.types';
import { PatientMedicinesWithReimbursementsTable } from '../shared/PatientMedicinesWithReimbursementsTable';
import { getMedicineName } from '../patientEntitlementsBenefitsCheck/PatientEntitlementBenefitsCheckModalContent';

import { formatReimbursement } from './patientReimbursementIndicationsCheck.utils';

type PatientEntitlementBenefitsCheckModalContentProps = {
   readonly prescriptionsWithoutIndications: DraftPrescriptionsFieldArray;
   readonly onConfirm: VoidFunction;
};

export const PatientReimbursementIndicationsCheckModalContent = (
   props: PatientEntitlementBenefitsCheckModalContentProps
): ReactElement => {
   const translate = useTranslate();
   const { prescriptionsWithoutIndications } = props;

   const medicinesWithReimbursements = useMemo(
      () =>
         prescriptionsWithoutIndications.map((prescription, index) => ({
            reimbursement: formatReimbursement(prescription, translate),
            medicineName: getMedicineName(prescription),
            id: index,
         })),
      [prescriptionsWithoutIndications, translate]
   );

   if (medicinesWithReimbursements.length === 1) {
      return (
         <StyledFlex gap={'8px'} direction={'column'}>
            <span>
               <Trans
                  i18nKey={draftPrescriptionsTranslationKeys.patientNotEligibleForReimbursementSingle}
                  components={{ bold: <StrongText testId={'emphasis'} /> }}
                  values={medicinesWithReimbursements.at(0)}
               />
            </span>
            <ModalConfirmButton
               label={draftPrescriptionsTranslationKeys.sendPrescriptionWithoutIndications}
               onClick={props.onConfirm}
            />
         </StyledFlex>
      );
   }

   return (
      <StyledFlex gap={'18px'} direction={'column'}>
         <Translate>{draftPrescriptionsTranslationKeys.patientNotEligibleForReimbursementMultiple}</Translate>
         <PatientMedicinesWithReimbursementsTable medicinesWithReimbursements={medicinesWithReimbursements} />
         <Translate>{draftPrescriptionsTranslationKeys.areYouSureSendingPrescriptionWithReimbursement}</Translate>
         <ModalConfirmButton
            label={draftPrescriptionsTranslationKeys.sendPrescriptionsWithoutIndications}
            onClick={props.onConfirm}
         />
      </StyledFlex>
   );
};
